import type { TranslationSchema } from './de';
const en: TranslationSchema = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    close: 'Close',
    all: 'All',
    none: 'None',
    name: 'Name',
    description: 'Description',
    color: 'Color',
    type: 'Type',
    yes: 'Yes',
    no: 'No',
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    import: 'Import',
    rename: 'Rename',
    removeAll: 'Remove all',
    selected: '{{count}} selected',
    resetFilters: 'Reset filters',
    noOptions: 'No options',
    general: 'General',
    escCancel: 'ESC Cancel',
    cmdEnterSave: 'Cmd+Enter Save',
    enterSave: 'Enter Save',
    escCmdEnter: 'ESC \u00b7 \u2318Enter',
    dragToReorder: 'Drag to reorder',
    dropHere: 'Drop here',
    moveTo: 'Move to:',
    dragOrClickToChangeTeam: 'Drag or click to change team',
  },
  nav: {
    roadmap: 'Roadmap',
    overview: 'Overview',
    columns: 'Columns',
    canvas: 'Canvas',
    editor: 'Editor',
    employees: 'Employees',
    categories: 'Categories',
    boards: 'Boards',
    tasks: 'Tasks',
    teams: 'Teams',
    skills: 'Skills',
    boardSection: 'Board',
    globalSection: 'Global',
    selectBoard: 'Select board...',
    newBoard: 'New Board',
    addTooltip: 'Add {{label}}',
    exportTooltip: 'Export',
    importTooltip: 'Import',
    adrs: 'ADRs',
    resources: 'Resources',
    searchBoards: 'Search boards...',
    noResults: 'No board found',
  },
  landing: {
    heroHeadline: 'Discovery. Roadmap. Daily.\nNo signup. Unlimited use.',
    heroSub: 'Canvas, timeline, PERT estimates, and skill matrix \u2014 connected instead of spread across four tools. No account, no cloud, no limits.',
    tryBoard: 'Try the board',
    createOwnBoard: 'Create your own board',
    featuresLabel: 'Tools',
    featuresHeading: 'What powers the workflow',
    featuresSub: 'Four tools that work together across every phase — on the board, in the roadmap, and in the overview. Changes take effect everywhere immediately.',
    pertTitle: 'PERT Estimates',
    pertDesc: 'Three estimates per task — or individually per person. Captured on the board, visible in the roadmap, aggregated as risk in the overview. From Robert C. Martin\u2019s "The Clean Coder".',
    interactiveRoadmap: 'Gantt Timeline',
    interactiveRoadmapDesc: 'The roadmap view: drag & drop, smooth zoom, four detail levels. Stakeholders see when deliverables will be ready and how changes affect the plan.',
    canvas: 'Canvas',
    canvasDesc: 'Tech trees, infrastructure, project structure — as a free-form canvas. Layers, groups, connections. The dependency explorer highlights individual paths.',
    teamScheduling: 'Skills & Conflicts',
    teamSchedulingDesc: 'Skill matrix with 3 levels and automatic task matching. Double bookings are detected across boards — before they become a problem.',
    tagEstimation: 'Estimation',
    tagPlanning: 'Roadmap',
    tagVisualization: 'Board',
    tagResources: 'Team',
    navFeatures: 'Features',
    navDemo: 'Demo',
    getStarted: 'Get Started',
    createCanvas: 'Create Canvas',
    createBoard: 'Create Board',
    playground: 'Playground',
  },
  boards: {
    title: 'Boards',
    createButton: 'Create board',
    emptyHint: 'Create a new board to get started.',
    boardNamePlaceholder: 'Board Name',
    boardNameInputPlaceholder: 'Board Name...',
    deleteConfirm: 'Really delete board? All tasks of this board will be lost.',
    employeeCount: '{{count}} employees',
    start: 'Start',
  },
  categories: {
    title: 'Categories',
    count: '{{count}} categories',
    addButton: 'Add category',
    noCategories: 'No categories',
    noCategoriesDesc: 'Create categories to organize tasks.',
    newPlaceholder: 'New category...',
    deleteConfirm: 'Delete category?',
    deleteConfirmWithTasks: 'Delete category? {{count}} tasks use this category. The category will be removed from all tasks.',
  },
  employees: {
    title: 'Employees',
    subtitle: '{{count}} employees in {{teamCount}} teams',
    addButton: 'Add employee',
    noEmployees: 'No employees',
    noEmployeesDesc: 'Add employees to form teams and assign tasks.',
    noEmployeesInTeam: 'No employees in this team',
    noEmployeesInSelectedTeam: 'No employees in the selected team',
    withoutTeam: 'No team',
    deleteConfirm: 'Really delete "{{name}}"?',
    yesDelete: 'Yes, delete',
    assignEmployee: 'Assign employee',
    allAssigned: 'All employees already assigned.',
    createNew: 'Create new employee',
    clickToAddEstimate: 'Click on an employee to add an estimate',
    addEmployeeHint: 'Emoji string for skills (multiples = higher level)',
    assigned: 'Assigned',
    assign: 'Assign',
    utilization: 'Utilization',
    weeks: 'Weeks',
    themesParallel: '{{count}} themes in parallel',
    efficiency: 'Efficiency:',
    optimalWeeks: 'optimal: {{weeks}}w',
    projects: 'Projects:',
  },
  teams: {
    title: 'Teams',
    subtitle: '{{count}} teams with {{employeeCount}} employees total',
    addButton: 'Add team',
    noTeams: 'No teams',
    noTeamsDesc: 'Create a team to organize employees.',
    noEmployees: 'No employees',
    deleteConfirm: 'Really delete team "{{name}}"?',
    deleteWarning: 'Warning: {{count}} employees are assigned to this team!',
    yesDelete: 'Yes, delete',
  },
  task: {
    newTask: 'New Task',
    editTask: 'Edit task',
    prevTask: 'Previous task',
    nextTask: 'Next task',
    description: 'Description',
    descriptionPlaceholder: 'Add description...',
    consequence: 'Consequence',
    consequencePlaceholder: 'Consequence (if not implemented)...',
    titlePlaceholder: 'Implement feature XYZ',
    estimate: 'Estimate',
    estimateModeTask: 'Task',
    estimateModeIndividual: 'Per employee',
    requiredSkills: 'Required Skills',
    calculations: 'Calculations',
    createAndClose: 'Create & Close',
    createAndNext: 'Create & next',
    created: '\u2713 "{{title}}" created',
    deleteGlobalConfirm: 'Delete task globally?',
    deleteFromBoard: 'Remove from board',
    allEstimates: 'All estimates (O/M/P)',
    teamAndCalculation: 'Team & Calculation',
    speed: 'Speed',
    risk: 'Risk',
    alone: '{{name}} alone',
    withTeam: 'With team',
    timeSaving: 'Time saved',
    skillsMissing: 'Skills insufficient \u2013 missing:',
    hideSensitive: 'Hide sensitive data',
    showSensitive: 'Show sensitive data',
    addTask: 'Add task',
    deleteTaskConfirm: 'Delete task globally? It will be removed from all boards.',
    deleteTaskButton: 'Delete task',
    noTasks: 'No tasks',
    noTasksDesc: 'Create a task to start planning.',
    searchPlaceholder: 'Search tasks...',
    totalTasks: '{{count}} tasks total',
    totalTasksFiltered: '{{count}} tasks total ({{filtered}} shown)',
    noTasksFound: 'No tasks found for the active filters.',
    noTasksExist: 'No tasks available.',
    notAssigned: 'Not assigned',
    assignEmployeeToTask: 'Assign employee',
    doubleClickToEdit: 'Double-click to edit',
    removeEmployee: 'Remove {{name}}',
    primaryResponsible: '(Primary)',
    additionalPercent: '(+10%)',
    unknownSkill: 'Unknown',
    skillsNotAssigned: 'NOT ASSIGNED',
    skillsFulfilled: 'SKILLS FULFILLED',
    skillsMissingHeader: 'SKILLS MISSING',
    bestCaseEnd: 'Best Case End',
    bestCase: 'Best Case',
  },
  taskTable: {
    title: 'Title',
    assignees: 'Assignees',
    boards: 'Boards',
    start: 'Start',
    estimate: 'Estimate',
    noBoard: 'No board',
    individualCount: '{{count}} Ind.',
    columns: 'Columns',
  },
  taskStatus: {
    all: 'All',
    onBoard: 'On board',
    noBoard: 'No board',
  },
  columns: {
    title: 'Columns',
    count: '{{count}} columns with {{taskCount}} tasks total',
    dragHint: 'Reorder columns via drag & drop',
    addButton: 'Add column',
    noColumns: 'No columns available.',
    addFirst: 'Add first column',
    defaultCategories: 'Default categories (task pool filter)',
    deleteConfirm: 'Really delete column "{{name}}"?',
    deleteWarning: 'Warning: {{count}} tasks are assigned to this column!',
    yesDelete: 'Yes, delete',
    noTasks: 'No tasks',
    taskCount: '{{count}} tasks',
    addTaskToColumn: 'Add task to {{column}}',
    columnNameLabel: 'Column Name',
    deleteColumn: 'Delete column',
    confirmDeleteShort: 'Really delete?',
    closeButton: 'Close',
    addColumnModal: {
      title: 'Add new column',
      nameRequired: 'Name must not be empty',
      nameExists: 'This column already exists',
      existing: 'Existing',
    },
  },
  skills: {
    title: 'Skills',
    subtitle: '{{count}} skills \u00b7 {{employeeCount}} employees',
    subtitleEmpty: 'Manage skills and skill distribution in the team',
    addSkillButton: 'Skill',
    fromPreset: 'From preset',
    addModal: {
      addTitle: 'Add new skill',
      editTitle: 'Edit skill',
      emojiLabel: 'Emoji',
      emojiPlaceholder: 'Choose emoji...',
      descriptionPlaceholder: 'Skill description...',
    },
    emptyNoSkillsNoEmployees: 'No skills or employees yet',
    emptyNoSkillsNoEmployeesDesc: 'Create employees first, then add skills.',
    emptyNoSkills: 'No skills defined',
    emptyNoSkillsDesc: 'Add skills to assign them to employees.',
    addFromPreset: 'Add from preset',
    singleSkill: 'Single skill',
    deleteConfirm: 'Really delete "{{name}}"?',
    noEmployeesAssigned: 'No employees assigned yet.',
    dropToAssign: 'Drop here to assign',
    dragToMoveSkill: 'Drag to move to another skill',
    removeAssignment: 'Remove assignment',
    collapse: 'Collapse',
    expand: 'Expand',
    employeesWithSkill: 'Employees with this skill ({{count}})',
    teamCoverage: 'Team Coverage',
    noCoverage: 'No team coverage available!',
    levelLegend: 'Level Legend',
    level3Desc: 'Can guide others and solve complex problems',
    level2Desc: 'Can work independently and handle medium tasks',
    level1Desc: 'Basic knowledge, needs support for complex tasks',
    deleteSkillConfirm: 'Really delete skill?',
    deleteSkillWarning: 'The skill "{{name}}" will be permanently deleted.',
    closeEsc: 'Close (Esc)',
  },
  overview: {
    title: 'Overview',
    stats: '{{taskCount}} tasks \u00b7 ~{{weekCount}} PW',
    columnsView: 'Columns',
    timelineView: 'Timeline',
    unprioritized: 'Unprioritized',
    unprioritizedCount: 'Unprioritized ({{count}})',
    unprioritizedUnmatched: 'Unprioritized (unmatched)',
    noActiveTasks: 'No active tasks available.',
    hardRequirements: 'Hard Requirements',
    hardRequirementsDesc: 'Prerequisite for project start',
    softRequirements: 'Soft Requirements',
    softRequirementsDesc: 'Affects implementation speed',
    noHardRequirements: 'No hard requirements available',
    noSoftRequirements: 'No soft requirements available',
    addHardRequirement: 'Add hard requirement',
    addSoftRequirement: 'Add soft requirement',
    requirementModal: {
      editTitle: 'Edit requirement',
      addTitle: 'New Global Requirement',
      namePlaceholder: 'e.g. Additional developer',
      descriptionPlaceholder: 'Why is this requirement needed?',
    },
    byDate: 'by {{kw}} ({{date}})',
    hardLabel: 'Hard:',
    softLabel: 'Soft:',
    calendarWeek: 'CW {{week}}',
    taskCount: '{{count}} tasks',
  },
  doubleBooking: {
    title: 'Double Bookings',
    weekOverlap_one: '{{count}} week overlap',
    weekOverlap_other: '{{count}} weeks overlap',
    assignedTo: 'Simultaneously assigned to:',
  },
  importModal: {
    title: 'Import view',
    placeholder: 'Paste JSON here...',
    invalidVersion: 'Invalid version',
    invalidFormat: 'Invalid format: tasks or allTasks missing',
    invalidJson: 'Invalid JSON',
    importButton: 'Import',
    escHint: 'ESC Cancel \u00b7 \u2318+Enter Import',
  },
  exportAlert: 'Data copied to clipboard',
  estimates: {
    days: 'Days',
    weeks: 'Weeks',
    months: 'Months',
    optimistic: 'Optimistic',
    realistic: 'Realistic',
    pessimistic: 'Pessimistic',
  },
  addEmployeeModal: {
    title: 'Add new employee',
    skillsLabel: 'Skills (Emojis)',
    skillsHint: 'Emoji string for skills (multiples = higher level)',
    teamLabel: 'Team',
  },
  addTeamModal: {
    title: 'Add new team',
  },
  canvas: {
    contentError: 'Content error',
    clearContent: 'Clear content',
    renderError: 'Render error',
  },
  board: {
    saveButton: 'Save',
    shiftSelected: 'Selected',
    shiftTasks: 'Shift tasks',
    shiftPresets: 'Presets',
    shiftLeft: 'days left',
    shiftRight: 'days right',
    customDays: 'Custom (days)',
    setStartDate: 'Set start date',
    moveButton: 'Move',
    moveHint: 'Earliest task → date, rest keeps spacing',
    daysPlaceholder: 'Days',
    moveToDateTitle: '{{target}} tasks: move to date (earliest task → date, rest relative)',
  },
  presets: {
    addFromPreset: 'Add from preset',
    selectFromGroups: 'Select skills from predefined groups',
    available: '{{count}} available',
    alreadyExists: 'already exists',
    selectedCount: '{{count}} skill(s) selected',
    noneSelected: 'No skills selected',
    addSelected: 'Add selected',
  },
  language: {
    de: 'Deutsch',
    en: 'English',
  },
  boardv3: {
    contextMenu: {
      assign: 'Assign',
      duplicate: 'Duplicate',
      splitAt: 'Split at {{date}}',
      removeFromBoard: 'Remove from board',
      delete: 'Delete',
      detailLevel: 'Detail level',
      addNewTask: 'Add new task',
      selectAll: 'Select all tasks',
      openTaskPool: 'Open task pool',
      editColumn: 'Edit column',
      deleteColumn: 'Delete column',
    },
    detailLevels: {
      auto: 'Automatic',
      minimal: 'Minimal',
      kompakt: 'Compact',
      normal: 'Normal',
      detailliert: 'Detailed',
    },
    headerMenu: {
      auto: 'Automatic',
      showYears: 'Show years',
      showMonths: 'Show months',
      showDays: 'Show days',
    },
    toolbar: {
      moveToolbar: 'Move toolbar (double-click = reset)',
      openToolbar: 'Open toolbar',
      shiftTasks: 'Shift tasks',
      undo: 'Undo',
      view: 'View',
      addNewTask: 'Add new task',
      openTaskPool: 'Open task pool',
      removeSelectedFromBoard: 'Remove selected tasks from board',
      deleteSelected: 'Delete selected tasks',
    },
    assigneePicker: {
      searchPlaceholder: 'Search by name, team, skills...',
      all: 'All',
      assigned: 'Assigned ({{count}})',
      available: 'Available ({{count}})',
      noEstimate: 'No estimate ({{count}})',
      noEmployeesFound: 'No employees found',
    },
    selection: {
      tasksSelected: '{{count}} tasks selected',
      removeFromSelection: 'Remove from selection',
      addToSelection: 'Add to selection',
      searchTask: 'Search task...',
      noResults: 'No results',
    },
    viewSettings: {
      navigation: 'Navigation',
      header: 'Header',
      sidebar: 'Sidebar',
      tasks: 'Tasks',
      toolbar: 'Toolbar',
      cwAlwaysVisible: 'CW always visible',
      show: 'show',
      hide: 'hide',
      on: 'On',
      off: 'Off',
    },
    taskPool: {
      all: 'All',
      searchTask: 'Search task...',
      noTasksFound: 'No tasks found for "{{query}}"',
      resetSearch: 'Reset search',
      allTasksOnBoard: 'All tasks are already on this board',
      noMatchingTasks: 'No matching tasks for the active filters',
      addToBoard: 'Add to board',
      add: 'Add',
    },
    sidebar: {
      expand: 'Expand sidebar',
    },
    columnHeader: {
      clickForOptions: '{{column}} – Click for options',
    },
  },
  canvasMenu: {
    deselect: 'Deselect',
    select: 'Select',
    copy: 'Copy',
    cut: 'Cut',
    duplicate: 'Duplicate',
    copyAsImage: 'Copy as image',
    background: 'Background',
    add: 'Add',
    paste: 'Paste',
    group: 'Group',
    selectAllNodes: 'Select all nodes',
    spriteClickThroughOff: 'Sprite click-through off',
    spriteClickThroughOn: 'Sprite click-through on',
    patternNone: 'None',
    patternDots: 'Dots',
    patternGrid: 'Grid',
    patternCrossDots: 'Cross dots',
    patternGraph: 'Graph paper',
    deleteConnection: 'Delete connection',
    deleteConnections: 'Delete {{count}} connections',
    split: 'Split',
    palette: 'Palette...',
    noBorder: 'No border',
    removeFromAllGroups: 'Remove from all groups',
    assignGroup: 'Assign group...',
    groupLabel: 'Group',
    removeCount: 'Remove {{count}} {{label}}',
    lineColor: 'Line color',
    lineType: 'Line type',
    lineStyle: 'Line style',
    lineWidth: 'Line width',
    arrowStart: 'Arrow start',
    arrowEnd: 'Arrow end',
    editLabel: 'Edit label',
    labelPrompt: 'Label:',
    removeLabel: 'Remove label',
    addLabel: 'Add label',
    segmentStraight: 'Straight',
  },
  canvasToolbar: {
    moveToolbar: 'Move toolbar (double-click = reset)',
    openToolbar: 'Open toolbar',
    rotate: 'Rotate',
    undo: 'Undo (Ctrl+Z)',
    redo: 'Redo (Ctrl+Shift+Z)',
    depExplorer: 'Explore dependencies',
    spriteClickThrough: 'Sprite click-through',
    addElement: 'Add element',
    removeNodes: 'Remove {{count}} node(s)',
    zoom: 'Zoom',
    fitContent: 'Fit to content',
    trees: 'Trees',
    deleteTreeConfirm: 'Delete tree "{{name}}"?',
    deleteTree: 'Delete "{{name}}"',
    newTree: 'New Tree',
    usedColors: 'Used',
    mixed: 'mixed',
    on: 'on',
    off: 'off',
  },
  canvasPicker: {
    assignGroup: 'Assign group',
    searchGroup: 'Search group...',
    noGroupsFound: 'No groups found',
    noGroups: 'No groups available',
    member: 'Member',
    addTask: 'Add task',
    searchTask: 'Search task...',
    noTasksFound: 'No tasks found',
    allTasksInTree: 'All tasks already in tree',
  },
  canvasLayer: {
    layers: 'Layers',
    addLayer: 'Add layer',
    empty: '(empty)',
    add: 'Add',
    hide: 'Hide',
    show: 'Show',
    unlock: 'Unlock',
    lock: 'Lock',
    moveUp: 'Move up',
    moveDown: 'Move down',
    removeLayer: 'Remove layer',
    image: 'Image',
    expandSidebar: 'Expand sidebar',
    hideDeps: 'Hide dependencies',
    showDeps: 'Show dependencies',
  },
  canvasSprite: {
    search: 'Search...',
    filterLibrary: 'Filter library',
    noResults: 'No results',
    loading: 'Loading sprites...',
    all: 'All',
    diagram: 'Diagram',
  },
  employeeDetail: {
    deleteTitle: 'Delete',
    closeEsc: 'Close (Esc)',
    confirmDelete: 'Really delete employee?',
    willBeDeleted: '"{{name}}" will be permanently deleted.',
    yesDelete: 'Yes, delete',
    color: 'Color',
    skillOverview: 'Skill Overview',
    totalPoints: 'Total Points',
    expertSkills: 'Expert Skills',
  },
  boardHeader: {
    exportAlert: 'View copied to clipboard!',
    compactTitle: 'Compact view (hides deprioritized tasks)',
    compactOn: 'Compact \u2713',
    compactOff: 'Compact',
    allTasks: 'All tasks:',
    shiftLeft: 'Shift all tasks one day left',
    shiftRight: 'Shift all tasks one day right',
    recalculate: 'Recalculate',
    export: 'Export',
    import: 'Import',
    zoom: 'Zoom:',
    zoomOut: 'Zoom out (Ctrl+-)',
    zoomIn: 'Zoom in (Ctrl++)',
    zoomReset: 'Reset zoom',
  },
  categorySelect: {
    removeCategory: 'Remove {{name}}',
    categories: 'Categories',
    noMore: 'No more categories',
    createNew: 'Create new category',
    noneAssigned: 'No categories assigned',
  },
  emojiPicker: {
    chooseEmoji: 'Choose emoji',
    searchEmoji: 'Search emoji...',
  },
  skillRequirements: {
    label: 'Required Skills',
    levelHint: 'Skill level (0 = not required, 3 = expert)',
  },
  estimatesEditor: {
    copyFromLast: 'Copy from last task',
  },
  canvasEntity: {
    // Shared color names
    colorGray: 'Gray',
    colorDark: 'Dark',
    colorBlue: 'Blue',
    colorGreen: 'Green',
    colorRed: 'Red',
    colorOrange: 'Orange',
    colorPurple: 'Purple',
    colorLight: 'Light',
    colorWhite: 'White',
    colorYellow: 'Yellow',
    colorLightBlue: 'Light blue',
    // Stroke styles
    strokeSolid: 'Solid',
    strokeLongDash: 'Long dash',
    strokeDashed: 'Dashed',
    strokeShortDash: 'Short dash',
    strokeDotted: 'Dotted',
    strokeDashDot: 'Dash-dot',
    strokeDashDotDot: 'Dash-dot-dot',
    // Arrow types
    arrowNone: 'None',
    arrowArrow: 'Arrow',
    arrowOpen: 'Open arrow',
    arrowTriangle: 'Triangle',
    arrowDiamond: 'Diamond',
    arrowCircle: 'Circle',
    arrowBar: 'Bar',
    arrowSquare: 'Square',
    // Border
    borderNone: 'None',
    noBorder: 'No border',
    // Toolbar labels
    background: 'Background',
    backgroundColor: 'Background color',
    textColor: 'Text color',
    borderColor: 'Border color',
    borderWidth: 'Border width',
    strokeWidth: 'Stroke width',
    cornerRadius: 'Corner radius',
    connectors: 'Connectors',
    shape: 'Shape',
    mode: 'Mode',
    fontSize: 'Font size',
    fontWeight: 'Font style',
    color: 'Color',
    size: 'Size',
    fixedStroke: 'Fixed stroke width',
    opacity: 'Opacity',
    collapse: 'Collapse',
    connectorType: 'Connector type',
    lineColor: 'Line color',
    lineStyle: 'Line style',
    arrowStart: 'Arrow start',
    arrowEnd: 'Arrow end',
    clickThrough: 'Click-through',
    // Context menu
    remove: 'Remove',
    edit: 'Edit',
    editText: 'Edit text',
    editGroupLabel: 'Edit label',
    editDescription: 'Edit description',
    connectorsOff: 'Connectors off',
    connectorsOn: 'Connectors on',
    deleteConnector: 'Delete connection',
    deleteWaypoint: 'Delete connector',
    removeFromTree: 'Remove from tree',
    showDependencies: 'Show dependencies',
    dissolveGroup: 'Dissolve group',
    expand: 'Expand',
    invisibleMode: 'Invisible mode',
    containerMode: 'Container mode',
    strokeScaling: 'Stroke width scaling',
    strokeFixed: 'Fix stroke width',
    customValue: 'Custom value',
    // Multi-delete labels
    connections: 'Connections',
    elements: 'Elements',
    nodes: 'Nodes',
    // Picker labels
    image: 'Image',
    group: 'Group',
    // Prompt texts
    groupLabelPrompt: 'Group label:',
    descriptionPrompt: 'Description:',
    // Sprite stroke widths
    strokeThin: 'Thin (1)',
    strokeLight: 'Light (1.5)',
    strokeNormal: 'Normal (2)',
    strokeMedium: 'Medium (3)',
    strokeThick: 'Thick (4)',
    // Click-through
    ctAlways: 'Always',
    ctNever: 'Never',
    // Font weight
    fontNormal: 'Normal',
    fontBold: 'Bold',
    // Corner radius
    round: 'Round',
    // Image border radius
    brNone: 'None (0)',
    brSmall: 'Small (4)',
    brMedium: 'Medium (8)',
    brLarge: 'Large (16)',
    // Shape types
    shapeRect: 'Rectangle',
    shapeEllipse: 'Ellipse',
    shapeDiamond: 'Diamond',
    shapeTriangle: 'Triangle',
    // Display modes
    modeContainer: 'Container',
    modeInvisible: 'Invisible',
    // Segment types
    segmentBezier: 'Bezier',
    segmentStraight: 'Straight',
    segmentL: 'L',
    segment45: '45°',
    segment90: '90°',
  },
  adr: {
    title: 'ADR',
    newAdr: 'New ADR',
    editAdr: 'Edit ADR',
    context: 'Context',
    decision: 'Decision',
    consequences: 'Consequences',
    contextPlaceholder: 'What problem is being addressed?',
    decisionPlaceholder: 'What was decided?',
    consequencesPlaceholder: 'What are the consequences?',
    scope: 'Scope',
    scopeGlobal: 'Global',
    scopeBoard: 'Board',
    scopeCanvas: 'Canvas',
    status: {
      label: 'Status',
      draft: 'Draft',
      proposed: 'Proposed',
      accepted: 'Accepted',
      deprecated: 'Deprecated',
      superseded: 'Superseded',
    },
    implStatus: {
      label: 'Implementation',
      pending: 'Pending',
      in_progress: 'In Progress',
      done: 'Done',
      abandoned: 'Abandoned',
    },
    relation: {
      label: 'Relations',
      supersedes: 'Supersedes',
      superseded_by: 'Superseded by',
      amends: 'Amends',
      amended_by: 'Amended by',
      relates_to: 'Relates to',
      add: 'Add relation',
      searchAdr: 'Search ADR...',
      noResults: 'No ADRs found',
    },
    person: {
      label: 'People',
      author: 'Author',
      responsible: 'Responsible',
      reviewer: 'Reviewer',
      add: 'Add person',
      searchEmployee: 'Search employee...',
    },
    comment: {
      label: 'Comments',
      reply: 'Reply',
      resolve: 'Resolved',
      unresolve: 'Reopen',
      addComment: 'Add comment',
      placeholder: 'Write a comment...',
      resolved: 'Resolved',
    },
    sidebar: {
      title: 'ADRs',
      search: 'Search ADR...',
      newAdr: 'New ADR',
      noResults: 'No ADRs found',
      global: 'Global',
      board: 'Board',
      canvas: 'Canvas',
    },
    detailSidebar: {
      title: 'ADR Detail',
      expandSidebar: 'Expand ADR detail',
    },
    trigger: {
      label: 'ADR Trigger',
      openAdr: 'Open ADR',
      showAdrs: 'Show ADRs',
      addAdr: 'Add ADR',
      removeAdr: 'Remove ADR',
      deleteTrigger: 'Delete trigger',
      color: 'Color',
    },
    overview: {
      subtitle: '{{count}} ADRs',
      subtitleFiltered: '{{count}} ADRs ({{filtered}} shown)',
      searchPlaceholder: 'Search ADR...',
      noAdrs: 'No ADRs',
      noAdrsDesc: 'Create an ADR to document architecture decisions.',
      noResults: 'No ADRs found for active filters.',
      addButton: 'New ADR',
      updated: 'Updated',
      scope: 'Scope',
    },
    deleteConfirm: 'Really delete ADR?',
    tabs: {
      general: 'General',
      content: 'Content',
      people: 'People',
      relations: 'Relations',
      comments: 'Comments',
    },
  },
  resource: {
    title: 'Resource',
    newResource: 'New Resource',
    editResource: 'Edit Resource',
    name: 'Name',
    category: 'Category',
    scope: 'Scope',
    scopeGlobal: 'Global',
    scopeBoard: 'Board',
    scopeCanvas: 'Canvas',
    field: {
      label: 'Fields',
      addField: 'Add Field',
      removeField: 'Remove Field',
      fieldName: 'Field Name',
      fieldType: 'Type',
      fieldUnit: 'Unit',
      fieldValue: 'Value',
      primary: 'Primary',
      showOnBadge: 'Show on badge',
      aggregation: 'Aggregation',
      interval: 'Interval',
      types: { text: 'Text', number: 'Number', currency: 'Currency', percentage: 'Percentage', duration: 'Duration' },
      aggregations: { sum: 'Sum', avg: 'Average', min: 'Minimum', max: 'Maximum', none: 'None' },
      intervals: { monthly: 'Monthly', yearly: 'Yearly', hourly: 'Hourly' },
    },
    catalog: { search: 'Search resource...', newBlank: 'New blank resource', noResults: 'No results' },
    sidebar: { search: 'Search resource...', newResource: 'New Resource', noResults: 'No resources found', global: 'Global', board: 'Board', canvas: 'Canvas' },
    detailSidebar: {
      title: 'Resource Detail',
      expandSidebar: 'Expand resource detail',
      openOverview: 'Open canvas overview',
      boardStats: 'Board Usage',
      usedOnCanvases: 'Used on {{count}} canvases',
      usedOnCanvas: 'Used on 1 canvas',
      totalAttachments: '{{count}} attachments total',
      canvasLabel: 'Canvas',
      attachments: 'Attachments',
      notUsed: 'Not used yet',
      notUsedHint: 'Drag this resource onto entities in the canvas.',
      aggregatedValues: 'Aggregated Values',
    },
    badge: { deleteBadge: 'Delete badge' },
    overview: {
      subtitle: '{{count}} Resources',
      subtitleFiltered: '{{count}} Resources ({{filtered}} shown)',
      searchPlaceholder: 'Search resource...',
      noResources: 'No resources',
      noResourcesDesc: 'Create a resource to define reusable infrastructure components.',
      noResults: 'No resources found for active filters.',
      addButton: 'New Resource',
    },
    canvasOverview: {
      title: 'Canvas Resource Overview',
      totalCost: 'Total Cost',
      totalResources: 'Resources',
      totalEntities: 'Entities',
      perMonth: '/month',
      perYear: '/year',
      name: 'Name',
      category: 'Category',
      entities: 'Entities',
      total: 'Total',
      noResources: 'No resources on this canvas. Drag resources from the sidebar onto entities.',
      noResults: 'No resources found for active filters.',
      allCategories: 'All Categories',
      allTypes: 'All Types',
      search: 'Search resource...',
    },
    deleteConfirm: 'Really delete resource?',
  },
};
export default en;
